// 📋 응답할 설문 상단 배너 (2026-10-08 형 "기존 tptalk 만족도 설문처럼 대상이 되면 설문하라고 표시") —
//   SurveyBanner(경영진 고정 설문)와 같은 모양(상단 중앙 pill). 서버 /forms/list?box=todo 가 대상·열림을 판정 — 미응답이 있을 때만 렌더.
//   누르면 마감이 가장 가까운 설문을 앱 전역 팝업(FormPopupHost)으로 연다. 응답하면 'tpt:form-answered' 로 다시 읽어 다음 설문/사라짐.
//   ✕ 는 이 세션(탭)에서만 숨김 — 지금 보이는 설문 묶음 기준이라 새 설문이 오면 다시 보인다. 모바일은 SurveyBanner 보다 한 칸 위.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { listForms, type FormItem } from '../../api/forms';
import { openFormPopup } from './FormPopupHost';
import { Icon } from '../icons';

const DISMISS_KEY = 'tpt-forms-banner-dismissed';
const RELOAD_MS = 30 * 60_000;

/** 마감까지 남은 날(KST 날짜 기준, 당일 0) — 마감일 없으면 null */
function daysLeft(endsAt: string | null): number | null {
	if (!endsAt) return null;
	const today = new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 10);
	return Math.round((Date.parse(endsAt) - Date.parse(today)) / 86_400_000);
}

export default function FormsBanner() {
	const { t } = useTranslation();
	const loc = useLocation();
	const onApp = loc.pathname.startsWith('/chat') || loc.pathname.startsWith('/projects') || loc.pathname.startsWith('/feeds') || loc.pathname.startsWith('/drive');
	const [items, setItems] = useState<FormItem[]>([]);
	const [dismissed, setDismissed] = useState(() => { try { return sessionStorage.getItem(DISMISS_KEY) || ''; } catch { return ''; } });
	useEffect(() => {
		if (!onApp) return;
		// 실패(미로그인·준회원 403 등)는 배너 없음으로 흡수
		const load = () => { void listForms('todo').then((x) => setItems(x.filter((f) => !f.answered))).catch(() => setItems([])); };
		load();
		const iv = window.setInterval(load, RELOAD_MS);
		window.addEventListener('tpt:form-answered', load);
		return () => { window.clearInterval(iv); window.removeEventListener('tpt:form-answered', load); };
	}, [onApp]);
	if (!onApp || !items.length) return null;
	const sig = items.map((f) => f._id).sort().join(',');
	if (dismissed === sig) return null;
	// 마감 가까운 순(마감일 없는 설문은 뒤로)
	const next = [...items].sort((a, b) => (a.endsAt || '9999').localeCompare(b.endsAt || '9999'))[0];
	const d = daysLeft(next.endsAt);
	return (
		<div className="fixed z-[1900] max-md:bottom-36 max-md:right-3 md:left-1/2 md:top-1.5 md:-translate-x-1/2" data-testid="forms-banner">
			<div className="flex items-center gap-1 rounded-full bg-violet-600 px-1 py-0.5 text-white shadow-lg ring-2 ring-white/70">
				<button type="button" onClick={() => openFormPopup(next._id)} title={next.title} data-testid="forms-banner-open"
					className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-ui-xs font-bold hover:bg-white/15">
					<Icon name="clipboard" size={12} /><span>{t('forms.banner', { count: items.length, defaultValue: '응답할 설문 {{count}}건' })}</span>
					{d !== null && (
						<span className="rounded-full bg-white/25 px-1.5 py-px text-[10px] font-bold" data-testid="forms-banner-dday">
							{d <= 0 ? t('survey.ddayToday') : t('survey.dday', { n: d })}
						</span>
					)}
				</button>
				<button type="button" onClick={() => { try { sessionStorage.setItem(DISMISS_KEY, sig); } catch { /* */ } setDismissed(sig); }} title={t('survey.later')}
					aria-label={t('survey.later') as string} className="rounded-full px-1.5 py-1 text-ui-xs text-white/80 hover:bg-white/15">✕</button>
			</div>
		</div>
	);
}
